const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const esc = value => String(value ?? '').replace(/[&<>"']/g, char => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
}[char]));

const num = value => {
  const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const usd = value => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0
}).format(num(value));

const dateValue = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const daysBetween = (start, end) => {
  if (!start || !end) return null;
  const a = new Date(start);
  const b = new Date(end);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  return Math.max(0, Math.round((b - a) / 86400000));
};

const acquiredDate = customer => customer.acquiredAt || customer.acquisitionCompletedAt || '';
const isAcquired = customer => {
  const raw = String(customer?.acquisitionStatus || customer?.status || '').toLowerCase();
  return raw.includes('acquired') || raw.includes('purchased') || raw.includes('completed');
};
const vehicle = customer => customer.vehicle || [customer.vehicleYear, customer.vehicleMake, customer.vehicleModel, customer.vehicleTrim].filter(Boolean).join(' ') || 'Vehicle';
const sourceName = customer => customer.source || customer.acquisitionSource || 'Unknown';
const totalGross = customer => num(customer.frontGross) + num(customer.backGross);
const sold = customer => Boolean(customer.soldAt || customer.retailSoldAt || customer.saleDate);
const soldDate = customer => customer.soldAt || customer.retailSoldAt || customer.saleDate || '';

const getToken = () => localStorage.getItem('token') || localStorage.getItem('vincentrix_token') || '';
const getDealerId = () => {
  try {
    for (const key of ['dealership', 'dealer', 'vincentrix_dealer']) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed?.id) return parsed.id;
    }
  } catch (error) {
    console.warn('Retail performance dealer lookup failed:', error);
  }
  return '';
};
const authHeaders = () => {
  const token = getToken();
  const dealerId = getDealerId();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(dealerId ? { 'X-Dealership-Id': dealerId } : {})
  };
};

let cache = [];
let observer;
let timer;

const fetchCustomers = async () => {
  const response = await fetch(`${API_URL}/api/customers`, { headers: authHeaders() });
  if (!response.ok) throw new Error(`Unable to load acquisition results (${response.status})`);
  const data = await response.json();
  return data.customers || [];
};

const saveCustomer = async (id, updates) => {
  const response = await fetch(`${API_URL}/api/customers/${id}`, {
    method: 'PUT', headers: authHeaders(), body: JSON.stringify(updates)
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || `Save failed (${response.status})`);
  }
  return response.json();
};

const ensureStyles = () => {
  if (document.getElementById('vc-retail-performance-styles')) return;
  const style = document.createElement('style');
  style.id = 'vc-retail-performance-styles';
  style.textContent = `
    .vc-rp-wrap{margin:0 0 16px;border:1px solid rgba(59,130,246,.25);border-radius:18px;background:linear-gradient(135deg,rgba(5,13,26,.98),rgba(6,20,35,.96));overflow:hidden;color:#fff;box-shadow:0 14px 38px rgba(0,0,0,.25);font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display",sans-serif}
    .vc-rp-head{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;padding:18px 20px;border-bottom:1px solid rgba(255,255,255,.07)}
    .vc-rp-kicker{font-size:10px;letter-spacing:1.7px;text-transform:uppercase;color:#60a5fa;font-weight:900}.vc-rp-title{font-size:19px;font-weight:900;margin-top:4px}.vc-rp-note{font-size:10px;color:rgba(255,255,255,.42);margin-top:4px}
    .vc-rp-refresh,.vc-rp-edit{border:1px solid rgba(96,165,250,.28);background:rgba(59,130,246,.1);color:#dbeafe;border-radius:9px;padding:8px 10px;font-size:10px;font-weight:900;cursor:pointer}
    .vc-rp-metrics{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px;padding:14px}.vc-rp-card{padding:13px;border-radius:13px;background:rgba(255,255,255,.035);border:1px solid rgba(255,255,255,.07)}.vc-rp-label{font-size:9px;letter-spacing:.9px;text-transform:uppercase;color:rgba(255,255,255,.42);font-weight:800}.vc-rp-value{font-size:21px;font-weight:900;margin-top:5px}.vc-rp-sub{font-size:9px;color:rgba(255,255,255,.35);margin-top:3px}
    .vc-rp-section{padding:0 14px 14px}.vc-rp-section-title{font-size:11px;font-weight:900;letter-spacing:.7px;text-transform:uppercase;color:#bfdbfe;padding:8px 2px}
    .vc-rp-table{overflow:auto;border:1px solid rgba(255,255,255,.06);border-radius:12px}.vc-rp-row{display:grid;grid-template-columns:2fr repeat(6,1fr) 96px;gap:8px;align-items:center;min-width:820px;padding:10px 12px;border-top:1px solid rgba(255,255,255,.055);font-size:10px}.vc-rp-row:first-child{border-top:0}.vc-rp-row.vc-rp-header{background:rgba(255,255,255,.025);font-size:9px;text-transform:uppercase;letter-spacing:.7px;color:rgba(255,255,255,.4);font-weight:900}.vc-rp-muted{color:rgba(255,255,255,.42)}.vc-rp-good{color:#86efac}.vc-rp-warn{color:#fde68a}
    .vc-rp-source-row{display:grid;grid-template-columns:2fr repeat(5,1fr);gap:8px;min-width:650px;padding:10px 12px;border-top:1px solid rgba(255,255,255,.055);font-size:10px}.vc-rp-source-row:first-child{border-top:0}.vc-rp-source-row.vc-rp-header{background:rgba(255,255,255,.025);font-size:9px;text-transform:uppercase;letter-spacing:.7px;color:rgba(255,255,255,.4);font-weight:900}
    .vc-rp-modal{position:fixed;inset:0;z-index:2200;background:rgba(0,0,0,.72);backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;padding:18px}.vc-rp-editor{width:min(700px,96vw);max-height:92vh;overflow:auto;background:linear-gradient(180deg,#07101f,#050b15);border:1px solid rgba(96,165,250,.28);border-radius:18px;padding:20px;box-shadow:0 30px 90px rgba(0,0,0,.65)}.vc-rp-fields{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:16px}.vc-rp-field label{display:block;font-size:10px;color:rgba(255,255,255,.48);margin-bottom:5px}.vc-rp-field input,.vc-rp-field select{width:100%;box-sizing:border-box;padding:11px;border-radius:9px;border:1px solid rgba(255,255,255,.11);background:#0b1322;color:#fff;font-size:12px}.vc-rp-actions{display:flex;justify-content:flex-end;gap:9px;margin-top:18px}.vc-rp-save{border:0;background:linear-gradient(135deg,#2563eb,#06b6d4);color:#fff;border-radius:9px;padding:10px 14px;font-weight:900;cursor:pointer}.vc-rp-close{border:1px solid rgba(255,255,255,.1);background:rgba(255,255,255,.04);color:#fff;border-radius:9px;padding:10px 14px;font-weight:800;cursor:pointer}
    @media(max-width:768px){.vc-rp-metrics{grid-template-columns:1fr 1fr}.vc-rp-head{flex-direction:column}.vc-rp-fields{grid-template-columns:1fr}}
  `;
  document.head.appendChild(style);
};

const card = (label, value, sub = '') => `<div class="vc-rp-card"><div class="vc-rp-label">${esc(label)}</div><div class="vc-rp-value">${value}</div>${sub ? `<div class="vc-rp-sub">${esc(sub)}</div>` : ''}</div>`;

const sourcePerformance = acquired => {
  const groups = new Map();
  acquired.forEach(customer => {
    const source = sourceName(customer);
    if (!groups.has(source)) groups.set(source, []);
    groups.get(source).push(customer);
  });
  return [...groups.entries()].map(([source, units]) => {
    const soldUnits = units.filter(sold);
    const gross = soldUnits.reduce((sum, item) => sum + totalGross(item), 0);
    const turns = soldUnits.map(item => item.daysToSell ?? daysBetween(acquiredDate(item), soldDate(item))).filter(value => Number.isFinite(Number(value)));
    return {
      source,
      acquired: units.length,
      sold: soldUnits.length,
      soldRate: units.length ? Math.round((soldUnits.length / units.length) * 100) : 0,
      totalGross: gross,
      avgGross: soldUnits.length ? gross / soldUnits.length : 0,
      avgTurn: turns.length ? Math.round(turns.reduce((a, b) => a + Number(b), 0) / turns.length) : null
    };
  }).sort((a, b) => b.totalGross - a.totalGross || b.sold - a.sold);
};

const renderEditor = customer => {
  document.querySelector('.vc-rp-modal')?.remove();
  const modal = document.createElement('div');
  modal.className = 'vc-rp-modal';
  modal.innerHTML = `
    <div class="vc-rp-editor">
      <div class="vc-rp-kicker">Acquisition → Sold</div>
      <div class="vc-rp-title">${esc(vehicle(customer))}</div>
      <div class="vc-rp-note">${esc(customer.name || 'Unknown seller')}${customer.vin ? ` • ${esc(customer.vin)}` : ''}</div>
      <div class="vc-rp-fields">
        <div class="vc-rp-field"><label>Acquisition Type</label><select data-rp="acquisitionType"><option value="purchase" ${customer.acquisitionType === 'purchase' ? 'selected' : ''}>Purchase</option><option value="trade" ${customer.acquisitionType === 'trade' ? 'selected' : ''}>Trade</option><option value="auction" ${customer.acquisitionType === 'auction' ? 'selected' : ''}>Auction</option><option value="other" ${customer.acquisitionType === 'other' ? 'selected' : ''}>Other</option></select></div>
        <div class="vc-rp-field"><label>Acquisition Source</label><input data-rp="source" value="${esc(sourceName(customer) === 'Unknown' ? '' : sourceName(customer))}" placeholder="Service Drive, KBB ICO, Street Purchase..."></div>
        <div class="vc-rp-field"><label>Acquired Date</label><input data-rp="acquiredAt" type="date" value="${dateValue(acquiredDate(customer))}"></div>
        <div class="vc-rp-field"><label>Purchase / ACV</label><input data-rp="purchasePrice" inputmode="decimal" value="${num(customer.purchasePrice) || ''}"></div>
        <div class="vc-rp-field"><label>Sale Status</label><select data-rp="saleDisposition"><option value="inventory" ${!sold(customer) ? 'selected' : ''}>In Inventory</option><option value="retail" ${customer.saleDisposition === 'retail' ? 'selected' : ''}>Retail Sold</option><option value="wholesale" ${customer.saleDisposition === 'wholesale' ? 'selected' : ''}>Wholesale Sold</option></select></div>
        <div class="vc-rp-field"><label>Sold Date</label><input data-rp="soldAt" type="date" value="${dateValue(soldDate(customer))}"></div>
        <div class="vc-rp-field"><label>Sale Price</label><input data-rp="salePrice" inputmode="decimal" value="${num(customer.salePrice) || ''}"></div>
        <div class="vc-rp-field"><label>Front Gross</label><input data-rp="frontGross" inputmode="decimal" value="${num(customer.frontGross) || ''}"></div>
        <div class="vc-rp-field"><label>Back Gross</label><input data-rp="backGross" inputmode="decimal" value="${num(customer.backGross) || ''}"></div>
        <div class="vc-rp-field"><label>Stock Number</label><input data-rp="stockNumber" value="${esc(customer.stockNumber || '')}"></div>
      </div>
      <div class="vc-rp-note" style="margin-top:12px">Total gross and turn time are calculated automatically from the actual sale.</div>
      <div class="vc-rp-actions"><button class="vc-rp-close">Cancel</button><button class="vc-rp-save">Save Performance</button></div>
      <div class="vc-rp-note vc-rp-status" style="margin-top:8px"></div>
    </div>`;
  document.body.appendChild(modal);
  modal.querySelector('.vc-rp-close').onclick = () => modal.remove();
  modal.onclick = event => { if (event.target === modal) modal.remove(); };
  modal.querySelector('.vc-rp-save').onclick = async () => {
    const values = {};
    modal.querySelectorAll('[data-rp]').forEach(input => { values[input.dataset.rp] = input.value; });
    const acquiredAt = values.acquiredAt ? new Date(`${values.acquiredAt}T12:00:00`).toISOString() : acquiredDate(customer);
    const soldAt = values.saleDisposition === 'inventory' || !values.soldAt ? '' : new Date(`${values.soldAt}T12:00:00`).toISOString();
    const frontGross = num(values.frontGross);
    const backGross = num(values.backGross);
    const gross = frontGross + backGross;
    const daysToSell = soldAt ? daysBetween(acquiredAt, soldAt) : null;
    const now = new Date().toISOString();
    const activity = Array.isArray(customer.acquisitionActivity) ? customer.acquisitionActivity : [];
    const event = {
      id: `retail-performance-${Date.now()}`,
      type: soldAt ? 'vehicle-sold' : 'inventory-performance',
      actor: 'Dealership Team',
      source: 'Acquisition Performance',
      title: soldAt ? `Acquired vehicle ${values.saleDisposition === 'wholesale' ? 'wholesaled' : 'retailed'}` : 'Acquired vehicle performance updated',
      detail: soldAt ? `${usd(gross)} total gross • ${daysToSell ?? '—'} days to sell` : 'Vehicle remains in inventory',
      timestamp: now
    };
    const status = modal.querySelector('.vc-rp-status');
    status.textContent = 'Saving…';
    try {
      const result = await saveCustomer(customer.id, {
        acquisitionType: values.acquisitionType,
        source: values.source,
        acquiredAt,
        purchasePrice: num(values.purchasePrice),
        saleDisposition: values.saleDisposition,
        soldAt: soldAt || null,
        salePrice: num(values.salePrice),
        frontGross,
        backGross,
        totalGross: gross,
        daysToSell,
        stockNumber: values.stockNumber,
        acquisitionActivity: [...activity, event].slice(-250),
        acquisitionPerformanceUpdatedAt: now
      });
      const saved = result.customer || { ...customer, ...values, acquiredAt, soldAt, frontGross, backGross, totalGross: gross, daysToSell };
      cache = cache.map(item => Number(item.id) === Number(customer.id) ? saved : item);
      status.textContent = 'Saved';
      setTimeout(() => { modal.remove(); render(); }, 300);
    } catch (error) {
      status.textContent = error.message || 'Unable to save performance.';
    }
  };
};

const render = () => {
  ensureStyles();
  const workspace = document.querySelector('.vc-acquisition-workspace');
  if (!workspace) return;

  let root = document.getElementById('vc-acquisition-retail-performance');
  if (!root) {
    root = document.createElement('section');
    root.id = 'vc-acquisition-retail-performance';
    root.className = 'vc-rp-wrap';
    const roi = document.getElementById('vc-acquisition-roi');
    if (roi?.nextSibling) workspace.insertBefore(root, roi.nextSibling);
    else if (roi) workspace.appendChild(root);
    else {
      const header = workspace.querySelector('.vc-acquisition-header');
      if (header?.nextSibling) workspace.insertBefore(root, header.nextSibling);
      else workspace.prepend(root);
    }
  }

  const acquired = cache.filter(isAcquired);
  const soldUnits = acquired.filter(sold);
  const gross = soldUnits.reduce((sum, item) => sum + totalGross(item), 0);
  const retailUnits = soldUnits.filter(item => item.saleDisposition !== 'wholesale');
  const turns = soldUnits.map(item => item.daysToSell ?? daysBetween(acquiredDate(item), soldDate(item))).filter(value => Number.isFinite(Number(value)));
  const avgTurn = turns.length ? Math.round(turns.reduce((a, b) => a + Number(b), 0) / turns.length) : null;
  const avgGross = soldUnits.length ? gross / soldUnits.length : 0;
  const inventoryCount = acquired.length - soldUnits.length;
  const sourceRows = sourcePerformance(acquired);

  root.innerHTML = `
    <div class="vc-rp-head"><div><div class="vc-rp-kicker">Acquisition Intelligence</div><div class="vc-rp-title">Acquisition → Retail Performance</div><div class="vc-rp-note">Know which cars and acquisition sources actually turn into profitable inventory.</div></div><button class="vc-rp-refresh">Refresh Results</button></div>
    <div class="vc-rp-metrics">
      ${card('Acquired Units', acquired.length, `${inventoryCount} currently in inventory`)}
      ${card('Sold Units', soldUnits.length, acquired.length ? `${Math.round((soldUnits.length / acquired.length) * 100)}% of acquired` : 'No acquired units yet')}
      ${card('Total Realized Gross', usd(gross), 'Front + back gross')}
      ${card('Avg Gross / Sold', soldUnits.length ? usd(avgGross) : '—', `${retailUnits.length} retail sold`)}
      ${card('Avg Turn', avgTurn === null ? '—' : `${avgTurn}d`, 'Acquired date → sold date')}
    </div>
    <div class="vc-rp-section"><div class="vc-rp-section-title">Source Performance</div><div class="vc-rp-table">
      <div class="vc-rp-source-row vc-rp-header"><div>Source</div><div>Acquired</div><div>Sold</div><div>Sold %</div><div>Total Gross</div><div>Avg Gross / Turn</div></div>
      ${sourceRows.length ? sourceRows.map(row => `<div class="vc-rp-source-row"><div><strong>${esc(row.source)}</strong></div><div>${row.acquired}</div><div>${row.sold}</div><div>${row.soldRate}%</div><div class="${row.totalGross > 0 ? 'vc-rp-good' : ''}">${usd(row.totalGross)}</div><div>${row.sold ? `${usd(row.avgGross)} • ${row.avgTurn ?? '—'}d` : '—'}</div></div>`).join('') : '<div style="padding:14px;color:rgba(255,255,255,.45);font-size:11px">No acquired vehicles yet.</div>'}
    </div></div>
    <div class="vc-rp-section"><div class="vc-rp-section-title">Acquired Vehicle Results</div><div class="vc-rp-table">
      <div class="vc-rp-row vc-rp-header"><div>Vehicle</div><div>Type</div><div>Source</div><div>ACV</div><div>Status</div><div>Gross</div><div>Turn</div><div></div></div>
      ${acquired.length ? acquired.slice().sort((a,b) => new Date(acquiredDate(b) || 0) - new Date(acquiredDate(a) || 0)).map(item => {
        const isSold = sold(item);
        const turn = item.daysToSell ?? daysBetween(acquiredDate(item), soldDate(item));
        return `<div class="vc-rp-row"><div><strong>${esc(vehicle(item))}</strong><div class="vc-rp-muted">${esc(item.stockNumber || item.vin || item.name || '')}</div></div><div>${esc(item.acquisitionType || 'purchase')}</div><div>${esc(sourceName(item))}</div><div>${usd(item.purchasePrice)}</div><div class="${isSold ? 'vc-rp-good' : 'vc-rp-warn'}">${isSold ? (item.saleDisposition === 'wholesale' ? 'Wholesale Sold' : 'Retail Sold') : 'In Inventory'}</div><div>${isSold ? usd(totalGross(item)) : '—'}</div><div>${isSold && turn !== null ? `${turn}d` : '—'}</div><div><button class="vc-rp-edit" data-rp-edit="${Number(item.id)}">${isSold ? 'Edit Result' : 'Add Sale'}</button></div></div>`;
      }).join('') : '<div style="padding:14px;color:rgba(255,255,255,.45);font-size:11px">Mark an opportunity Acquired to start tracking its retail performance.</div>'}
    </div></div>`;

  root.querySelector('.vc-rp-refresh').onclick = refresh;
  root.querySelectorAll('[data-rp-edit]').forEach(button => {
    button.onclick = () => {
      const customer = cache.find(item => Number(item.id) === Number(button.dataset.rpEdit));
      if (customer) renderEditor(customer);
    };
  });
};

const refresh = async () => {
  if (!document.querySelector('.vc-acquisition-workspace')) return;
  try {
    cache = await fetchCustomers();
    render();
  } catch (error) {
    console.warn('VinCentrix acquisition retail performance refresh failed:', error);
  }
};

const boot = () => {
  ensureStyles();
  observer = new MutationObserver(() => {
    if (document.querySelector('.vc-acquisition-workspace') && !document.getElementById('vc-acquisition-retail-performance')) refresh();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  timer = window.setInterval(() => {
    if (document.visibilityState === 'visible' && document.querySelector('.vc-acquisition-workspace')) refresh();
  }, 60000);
  window.addEventListener('focus', () => {
    if (document.querySelector('.vc-acquisition-workspace')) refresh();
  });
  refresh();
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();
